import { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react'
import { useStore } from '@tanstack/react-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { MonthPicker } from '@/components/ui/month-picker'
import { AlertCircle, Info } from 'lucide-react'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { serviciosService, type ClienteServicioPrecio } from '@/services/serviciosService'
import type { Movimiento, Cuenta, Servicio } from '@/types/database'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'
import {
  useMovimientoForm,
  getFieldError,
  formatearNumeroAR,
  parsearNumeroAR,
  getPeriodoActual,
  type MovimientoFormValues,
} from './forms'
import type { MovimientoForm as MovimientoFormType } from './forms'

// Re-export para compatibilidad
export type { MovimientoFormValues }

interface MovimientoFormProps {
  movimiento?: Movimiento | null
  clientes: VistaSaldoClienteConImpuestos[]
  cuentas: Cuenta[]
  onSubmit: (data: MovimientoFormValues) => void
  onCancel?: () => void
  isLoading?: boolean
  clienteIdFijo?: string
  tipoFijo?: 'Deuda' | 'Pago'
  hideButtons?: boolean
  /** Receives whether the form has unsaved changes. Reports false on unmount. */
  onDirtyChange?: (dirty: boolean) => void
}

// Componente reutilizable para input de monto formateado
interface MontoFormateadoInputProps {
  value: number | undefined | null
  onChange: (value: number) => void
  placeholder?: string
  error?: string
}

function MontoFormateadoInput({ value, onChange, placeholder = '0', error }: MontoFormateadoInputProps) {
  const [displayValue, setDisplayValue] = useState<string>(
    value ? formatearNumeroAR(value) : ''
  )

  // Sync display cuando value cambia externamente (ej: auto-fill desde precio de servicio)
  useEffect(() => {
    if (value !== undefined && value !== null && value > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync prop→derived display state
      setDisplayValue(formatearNumeroAR(value))
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorIngresado = e.target.value
    setDisplayValue(valorIngresado)
    const numero = parsearNumeroAR(valorIngresado)
    onChange(numero)
  }

  const handleBlur = () => {
    const numero = parsearNumeroAR(displayValue)
    if (numero > 0) {
      setDisplayValue(formatearNumeroAR(numero))
    }
  }

  return (
    <>
      <Input
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </>
  )
}

// Componente interno para formulario de pago simplificado
interface FormularioPagoSimplificadoProps {
  form: MovimientoFormType
  cuentas: Cuenta[]
  montoInicial?: number | null
}

function FormularioPagoSimplificado({
  form,
  cuentas,
  montoInicial,
}: FormularioPagoSimplificadoProps) {
  const mesAnio = useStore(form.store, (s) => s.values.mes_anio)
  const cuentaId = useStore(form.store, (s) => s.values.cuenta_id)

  // Estado local para el monto formateado
  const [montoFormateado, setMontoFormateado] = useState<string>(
    montoInicial ? formatearNumeroAR(montoInicial) : ''
  )

  // Handler para cambio de monto
  const handleMontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorIngresado = e.target.value
    setMontoFormateado(valorIngresado)
    const numero = parsearNumeroAR(valorIngresado)
    form.setFieldValue('haber', numero)
  }

  // Al perder foco, formatear
  const handleMontoBlur = () => {
    const numero = parsearNumeroAR(montoFormateado)
    if (numero > 0) {
      setMontoFormateado(formatearNumeroAR(numero))
    }
  }

  return (
    <>
      {/* Periodo (opcional para pagos) */}
      <div className="space-y-2">
        <Label>Periodo (opcional)</Label>
        <MonthPicker
          value={mesAnio ?? null}
          onValueChange={(value) => form.setFieldValue('mes_anio', value)}
          placeholder="Seleccionar periodo"
        />
      </div>

      {/* Concepto */}
      <form.Field name="concepto">
        {(field) => (
          <div className="space-y-2">
            <Label>Concepto *</Label>
            <Input
              placeholder="Ej: Pago honorarios Diciembre 2025"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive">
                {getFieldError(field.state.meta.errors)}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {/* Monto con formato */}
      <div className="space-y-2">
        <Label>Monto *</Label>
        <Input
          type="text"
          inputMode="decimal"
          placeholder="0"
          value={montoFormateado}
          onChange={handleMontoChange}
          onBlur={handleMontoBlur}
        />
      </div>

      {/* Metodo de pago */}
      <div className="space-y-2">
        <Label>Metodo de pago</Label>
        <Select
          value={cuentaId ?? ''}
          onValueChange={(value) => form.setFieldValue('cuenta_id', value || null)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar metodo de pago" />
          </SelectTrigger>
          <SelectContent>
            {cuentas.map((cuenta) => (
              <SelectItem key={cuenta.id} value={cuenta.id}>
                {cuenta.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

export const MovimientoForm = forwardRef<HTMLFormElement, MovimientoFormProps>(
  function MovimientoForm({
    movimiento,
    clientes,
    cuentas,
    onSubmit,
    onCancel,
    isLoading,
    clienteIdFijo,
    tipoFijo,
    hideButtons = false,
    onDirtyChange,
  }, ref) {
    const hoy = new Date().toISOString().split('T')[0]
    const { supabase } = useSupabase()
    const { organizacionId } = useOrganizacion()
    // Sin verHonorariosCliente el catálogo llega sin precio y la deuda no se autocompleta con él.
    const { puede, permisosListos } = usePermissions()
    const incluirPrecio = puede.verHonorariosCliente === true
    const formRef = useRef<HTMLFormElement>(null)

    // Determinar tipo de formulario (nuevo o edicion)
    const esEdicion = !!movimiento

    // Logica para determinar que formulario mostrar
    const esDeudaEsporadica = esEdicion
      ? (movimiento.tipo === 'Deuda' && (movimiento.tipo_deuda === 'esporadica' || movimiento.tipo_deuda === null))
      : tipoFijo === 'Deuda'

    const esDeudaMensual = esEdicion
      ? (movimiento.tipo === 'Deuda' && movimiento.tipo_deuda === 'mensual')
      : false

    const esPago = esEdicion
      ? movimiento.tipo === 'Pago'
      : tipoFijo === 'Pago'

    const mostrarFormularioDeudaEsporadica = esDeudaEsporadica
    const mostrarFormularioDeudaMensual = esDeudaMensual
    const mostrarFormularioPago = esPago
    const mostrarFormularioGenerico = !mostrarFormularioDeudaEsporadica && !mostrarFormularioDeudaMensual && !mostrarFormularioPago

    // Periodo por defecto: actual para deudas esporadicas nuevas
    const periodoDefault = esEdicion
      ? movimiento.mes_anio
      : (tipoFijo === 'Deuda' ? getPeriodoActual() : null)

    const { form } = useMovimientoForm({
      defaultValues: {
        cliente_id: clienteIdFijo ?? movimiento?.cliente_id ?? '',
        concepto: movimiento?.concepto ?? '',
        fecha: movimiento?.fecha ?? hoy,
        debe: movimiento?.debe ?? null,
        haber: movimiento?.haber ?? null,
        tipo: tipoFijo ?? (movimiento?.tipo as 'Deuda' | 'Pago') ?? 'Deuda',
        tipo_deuda: (movimiento?.tipo_deuda as 'mensual' | 'esporadica') ?? 'esporadica',
        servicio_id: movimiento?.servicio_id ?? null,
        cuenta_id: movimiento?.cuenta_id ?? null,
        mes_anio: movimiento?.mes_anio ?? periodoDefault ?? null,
        observaciones: movimiento?.observaciones ?? null,
      },
      onSubmit: (data) => {
        // Parsear valores numericos
        const debe = data.debe ?? 0
        const haber = data.haber ?? 0

        // Para formularios simplificados sin campo fecha, usar fecha de hoy si no hay fecha
        const fecha = (mostrarFormularioDeudaMensual || mostrarFormularioPago) && !esEdicion
          ? hoy
          : (data.fecha || movimiento?.fecha || hoy)

        onSubmit({
          ...data,
          fecha,
          debe: data.tipo === 'Deuda' ? debe : 0,
          haber: data.tipo === 'Pago' ? haber : 0,
          tipo_deuda: data.tipo === 'Deuda' ? (data.tipo_deuda ?? 'esporadica') : null,
          servicio_id: data.servicio_id ?? null,
          cuenta_id: data.cuenta_id ?? null,
          mes_anio: data.mes_anio ?? null,
          observaciones: data.observaciones ?? null,
        })
      },
    })

    // Exponer el form ref para submit externo
    useImperativeHandle(ref, () => formRef.current!)

    const isDirty = useStore(form.store, (s) => s.isDirty)
    useEffect(() => {
      onDirtyChange?.(isDirty)
      return () => onDirtyChange?.(false)
    }, [isDirty, onDirtyChange])

    // Leer valores reactivos del form
    const tipo = useStore(form.store, (s) => s.values.tipo)
    const tipoDeuda = useStore(form.store, (s) => s.values.tipo_deuda)
    const clienteId = useStore(form.store, (s) => s.values.cliente_id)
    const servicioId = useStore(form.store, (s) => s.values.servicio_id)
    const mesAnio = useStore(form.store, (s) => s.values.mes_anio)
    const cuentaIdGenerico = useStore(form.store, (s) => s.values.cuenta_id)

    // Estado local para servicios disponibles y precios del cliente
    const [serviciosDisponibles, setServiciosDisponibles] = useState<Servicio[]>([])
    const [serviciosCliente, setServiciosCliente] = useState<(ClienteServicioPrecio & { servicio_nombre: string })[]>([])
    const [clienteTieneServiciosConfigurados, setClienteTieneServiciosConfigurados] = useState(false)
    const [cargandoServicios, setCargandoServicios] = useState(false)

    // Cargar servicios disponibles, con los permisos resueltos: el select depende de verHonorariosCliente.
    useEffect(() => {
      if (!supabase || !organizacionId || !permisosListos) return

      let vigente = true
      const cargarServicios = async () => {
        try {
          const todosLosServicios = await serviciosService.listar(supabase, organizacionId, incluirPrecio)
          const serviciosActivos = todosLosServicios.filter(
            (s) => s.estado === 'activo' && s.periodicidad === 'unico'
          )
          if (vigente) setServiciosDisponibles(serviciosActivos)
        } catch (error) {
          console.error('[MovimientoForm] Error cargando servicios:', error)
        }
      }
      cargarServicios()
      // Al cambiar de organización, la respuesta tardía de la anterior se descarta y la lista queda vacía hasta la nueva.
      return () => {
        vigente = false
        setServiciosDisponibles([])
      }
    }, [supabase, organizacionId, incluirPrecio, permisosListos])

    // Cargar servicios del cliente con precios
    useEffect(() => {
      if (!clienteId || !supabase) {
        setClienteTieneServiciosConfigurados(false)
        setServiciosCliente([])
        return
      }

      const cargarServiciosCliente = async () => {
        setCargandoServicios(true)
        try {
          const servicios = await serviciosService.obtenerServiciosCliente(supabase, clienteId)
          setServiciosCliente(servicios)
          setClienteTieneServiciosConfigurados(servicios.length > 0)
        } catch (error) {
          console.error('[MovimientoForm] Error cargando servicios del cliente:', error)
          setServiciosCliente([])
          setClienteTieneServiciosConfigurados(false)
        } finally {
          setCargandoServicios(false)
        }
      }
      cargarServiciosCliente()
    }, [clienteId, supabase])

    // Auto-generar concepto cuando cambia servicio o periodo (solo para deuda esporadica nueva)
    useEffect(() => {
      if (!mostrarFormularioDeudaEsporadica || esEdicion) return

      const servicioSeleccionado = serviciosDisponibles.find(s => s.id === servicioId)
      if (servicioSeleccionado && mesAnio) {
        const concepto = `${servicioSeleccionado.nombre} - ${mesAnio}`
        form.setFieldValue('concepto', concepto)
      }
    }, [servicioId, mesAnio, serviciosDisponibles, mostrarFormularioDeudaEsporadica, esEdicion, form])

    // Pre-llenar precio cuando cambia el servicio (solo para deuda esporadica nueva)
    useEffect(() => {
      if (!mostrarFormularioDeudaEsporadica || esEdicion) return

      if (servicioId && incluirPrecio) {
        const precioCliente = serviciosCliente.find(sc => sc.servicio_id === servicioId)
        if (precioCliente) {
          form.setFieldValue('debe', precioCliente.precio)
        } else {
          const servicio = serviciosDisponibles.find(s => s.id === servicioId)
          if (servicio?.precio_estimado) {
            form.setFieldValue('debe', servicio.precio_estimado)
          }
        }
      }
    }, [servicioId, serviciosCliente, serviciosDisponibles, mostrarFormularioDeudaEsporadica, esEdicion, form, incluirPrecio])

    // Obtener precio sugerido para mostrar hint. Sin verHonorariosCliente no hay sugerencia.
    const getPrecioSugerido = (): number | null => {
      if (!servicioId || !incluirPrecio) return null
      const precioCliente = serviciosCliente.find(sc => sc.servicio_id === servicioId)
      if (precioCliente) return precioCliente.precio
      const servicio = serviciosDisponibles.find(s => s.id === servicioId)
      return servicio?.precio_estimado ?? null
    }

    const precioSugerido = getPrecioSugerido()

    return (
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-6"
      >
        {/* Cliente */}
        {!clienteIdFijo && (
          <form.Field name="cliente_id">
            {(field) => (
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <ClienteCombobox
                  clientes={clientes}
                  value={field.state.value ?? ''}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder="Buscar cliente por nombre, CUIT o codigo..."
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        )}

        {/* Tipo de movimiento - Solo mostrar en formulario generico */}
        {mostrarFormularioGenerico && (
          <div className="space-y-2">
            <Label>Tipo *</Label>
            <Select
              value={tipo}
              onValueChange={(value: 'Deuda' | 'Pago') => {
                form.setFieldValue('tipo', value)
                // Reset valores segun tipo
                if (value === 'Deuda') {
                  form.setFieldValue('haber', null)
                } else {
                  form.setFieldValue('debe', null)
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Deuda">Deuda (Debe)</SelectItem>
                <SelectItem value="Pago">Pago (Haber)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tipo de deuda - Solo mostrar en formulario generico cuando es Deuda */}
        {mostrarFormularioGenerico && tipo === 'Deuda' && (
          <div className="space-y-2">
            <Label>Tipo de deuda *</Label>
            <Select
              value={tipoDeuda ?? 'esporadica'}
              onValueChange={(value: 'mensual' | 'esporadica') => form.setFieldValue('tipo_deuda', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="esporadica">
                  <div className="flex flex-col">
                    <span className="font-medium">Esporadica</span>
                    <span className="text-xs text-muted-foreground">Trabajo especial o puntual (unico)</span>
                  </div>
                </SelectItem>
                <SelectItem value="mensual">
                  <div className="flex flex-col">
                    <span className="font-medium">Mensual</span>
                    <span className="text-xs text-muted-foreground">Honorarios recurrentes (generados automaticamente)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Las deudas <strong>mensuales recurrentes</strong> se generan automaticamente mediante el lote mensual.
              Usa <strong>Esporadica</strong> solo para trabajos puntuales que no son parte de los servicios mensuales del cliente.
            </p>

            {/* Warning: Cliente con servicios configurados + tipo mensual manual */}
            {tipoDeuda === 'mensual' && clienteTieneServiciosConfigurados && !cargandoServicios && (
              <Alert variant="default" className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
                <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                <AlertDescription className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Atencion:</strong> Este cliente tiene servicios mensuales configurados.
                  Las deudas mensuales se generan automaticamente el dia 1 de cada mes.
                  ¿Estas seguro de crear una deuda mensual manual?
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* ===== FORMULARIO SIMPLIFICADO PARA DEUDA ESPORADICA ===== */}
        {mostrarFormularioDeudaEsporadica && (
          <>
            {/* Fecha */}
            <form.Field name="fecha">
              {(field) => (
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Servicio - REQUERIDO para nueva, editable para existente */}
            <div className="space-y-2">
              <Label>Servicio *</Label>
              <Select
                value={servicioId ?? ''}
                onValueChange={(value) => form.setFieldValue('servicio_id', value || null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar servicio" />
                </SelectTrigger>
                <SelectContent>
                  {serviciosDisponibles.map((servicio) => (
                    <SelectItem key={servicio.id} value={servicio.id}>
                      {servicio.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!servicioId && !esEdicion && (
                <p className="text-xs text-muted-foreground">
                  Selecciona el servicio del catalogo para este trabajo
                </p>
              )}
            </div>

            {/* Periodo - REQUERIDO */}
            <div className="space-y-2">
              <Label>Periodo *</Label>
              <MonthPicker
                value={mesAnio ?? null}
                onValueChange={(value) => form.setFieldValue('mes_anio', value)}
                placeholder="Seleccionar mes/año"
              />
              {!esEdicion && (
                <p className="text-xs text-muted-foreground">
                  Mes y año al que corresponde este trabajo
                </p>
              )}
            </div>

            {/* Concepto - Auto-generado pero editable */}
            <form.Field name="concepto">
              {(field) => (
                <div className="space-y-2">
                  <Label>Concepto *</Label>
                  <Input
                    placeholder={esEdicion ? "Concepto del movimiento" : "Se genera automaticamente al seleccionar servicio y periodo"}
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  {!esEdicion && servicioId && mesAnio && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Concepto auto-generado. Puedes editarlo si es necesario.
                    </p>
                  )}
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Precio */}
            <form.Field name="debe">
              {(field) => (
                <div className="space-y-2">
                  <Label>Precio *</Label>
                  <MontoFormateadoInput
                    value={field.state.value}
                    onChange={(value) => field.handleChange(value)}
                    placeholder="0"
                    error={field.state.meta.errors.length > 0 ? getFieldError(field.state.meta.errors) : undefined}
                  />
                  {!esEdicion && precioSugerido && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Precio sugerido del catalogo: ${precioSugerido.toLocaleString('es-AR')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          </>
        )}

        {/* ===== FORMULARIO SIMPLIFICADO PARA DEUDA MENSUAL ===== */}
        {mostrarFormularioDeudaMensual && (
          <>
            {/* Periodo - REQUERIDO */}
            <div className="space-y-2">
              <Label>Periodo *</Label>
              <MonthPicker
                value={mesAnio ?? null}
                onValueChange={(value) => form.setFieldValue('mes_anio', value)}
                placeholder="Seleccionar mes/año"
              />
            </div>

            {/* Concepto */}
            <form.Field name="concepto">
              {(field) => (
                <div className="space-y-2">
                  <Label>Concepto *</Label>
                  <Input
                    placeholder="Ej: Honorarios mensuales"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Monto */}
            <form.Field name="debe">
              {(field) => (
                <div className="space-y-2">
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={field.state.value ?? ''}
                    onChange={(e) => {
                      const val = e.target.value
                      field.handleChange(val === '' ? null : parseFloat(val))
                    }}
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Info sobre deudas mensuales */}
            <Alert variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
                Las deudas mensuales normalmente se generan automaticamente.
                Esta edicion es solo para ajustes manuales.
              </AlertDescription>
            </Alert>
          </>
        )}

        {/* ===== FORMULARIO SIMPLIFICADO PARA PAGO ===== */}
        {mostrarFormularioPago && (
          <FormularioPagoSimplificado
            form={form}
            cuentas={cuentas}
            montoInicial={movimiento?.haber}
          />
        )}

        {/* ===== FORMULARIO GENERICO ===== */}
        {mostrarFormularioGenerico && (
          <>
            {/* Servicio (opcional, solo para deudas esporadicas) */}
            {tipo === 'Deuda' && tipoDeuda === 'esporadica' && (
              <div className="space-y-2">
                <Label>Servicio (Opcional)</Label>
                <Select
                  value={servicioId ?? 'sin-servicio'}
                  onValueChange={(value) => form.setFieldValue('servicio_id', value === 'sin-servicio' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Asociar a un servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin-servicio">Sin servicio</SelectItem>
                    {serviciosDisponibles.map((servicio) => (
                      <SelectItem key={servicio.id} value={servicio.id}>
                        {servicio.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Puedes asociar esta deuda a un servicio para mejor trazabilidad en reportes.
                </p>
              </div>
            )}

            {/* Concepto */}
            <form.Field name="concepto">
              {(field) => (
                <div className="space-y-2">
                  <Label>Concepto *</Label>
                  <Input
                    placeholder="Ej: Honorarios Noviembre 2024"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fecha */}
              <form.Field name="fecha">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Fecha *</Label>
                    <Input
                      type="date"
                      value={field.state.value ?? ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Mes/Año (opcional) */}
              <div className="space-y-2">
                <Label>Periodo</Label>
                <MonthPicker
                  value={mesAnio ?? null}
                  onValueChange={(value) => form.setFieldValue('mes_anio', value)}
                  placeholder="Seleccionar periodo"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Monto (Debe o Haber segun tipo) */}
              <form.Field name={tipo === 'Deuda' ? 'debe' : 'haber'}>
                {(field) => (
                  <div className="space-y-2">
                    <Label>
                      {tipo === 'Deuda' ? 'Monto (Debe) *' : 'Monto (Haber) *'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={field.state.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value
                        field.handleChange(val === '' ? null : parseFloat(val))
                      }}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Metodo de pago (solo para pagos) */}
              {tipo === 'Pago' && (
                <div className="space-y-2">
                  <Label>Metodo de pago</Label>
                  <Select
                    value={cuentaIdGenerico ?? ''}
                    onValueChange={(value) => form.setFieldValue('cuenta_id', value || null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar metodo de pago" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuentas.map((cuenta) => (
                        <SelectItem key={cuenta.id} value={cuenta.id}>
                          {cuenta.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </>
        )}

        {/* Observaciones - Siempre visible */}
        <form.Field name="observaciones">
          {(field) => (
            <div className="space-y-2">
              <Label>Observaciones</Label>
              <Textarea
                placeholder="Notas adicionales sobre este movimiento (opcional)"
                rows={3}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
              />
            </div>
          )}
        </form.Field>

        {/* Botones - solo si no estan ocultos */}
        {!hideButtons && (
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading || (mostrarFormularioDeudaEsporadica && !esEdicion && (!servicioId || !mesAnio))}
            >
              {isLoading ? 'Guardando...' : esEdicion ? 'Actualizar' : 'Crear Movimiento'}
            </Button>
          </div>
        )}
      </form>
    )
  }
)
